import * as Sentry from '@sentry/react';
import { Quiz, QuizTag } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import Input from '@superfunnel/ui-core/v2/Input';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { onSnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import {
  EditPencil,
  Filter,
  MoreVert,
  MultiplePagesEmpty,
  Plus,
  Search,
  Trash,
} from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import {
  QuizFunnelsTable,
  QuizTableItem,
} from '../components/QuizFunnelsTable';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import HtmlStepperEditorModal from '../modals/HtmlStepperEditorModal';
import QuizFunnelsFilterPopover, {
  PartialQuizFilters,
} from '../popovers/QuizFunnelsFilterPopover';
import {
  createQuiz,
  deleteQuiz,
  duplicateQuiz,
  quizzesQuery,
} from '../services/QuizService';
import { track } from '../utils/analytics';

const QUIZ_FUNNELS_CONFIG = 'quizFunnelsFilters';

interface QuizFilters {
  tags: QuizTag[];
  statuses: Quiz['status'][];
  changes: boolean[];
}

function filterFromSearchParams(
  searchParams: URLSearchParams,
): PartialQuizFilters {
  const filters: PartialQuizFilters = {};

  const tags =
    searchParams.get('tags') ||
    localStorage.getItem(`${QUIZ_FUNNELS_CONFIG}:tags`);
  if (tags) {
    try {
      filters.tags = JSON.parse(tags);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing tags from search params', e);
    }
  }

  const statuses =
    searchParams.get('statuses') ||
    localStorage.getItem(`${QUIZ_FUNNELS_CONFIG}:statuses`);
  if (statuses) {
    try {
      filters.statuses = JSON.parse(statuses);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing statuses from search params', e);
    }
  }

  const changes =
    searchParams.get('changes') ||
    localStorage.getItem(`${QUIZ_FUNNELS_CONFIG}:changes`);
  if (changes) {
    try {
      filters.changes = JSON.parse(changes);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing changes from search params', e);
    }
  }

  return filters;
}

export default function QuizFunnels() {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { analyticsType, setAnalyticsType } = useContext(DashboardContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [quizzes, setQuizzes] = useState<
    QueryDocumentSnapshot<Quiz>[] | undefined
  >(undefined);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(
    (searchParams.get('search') ||
      localStorage.getItem(`${QUIZ_FUNNELS_CONFIG}:search`)) ??
      '',
  );
  const [filters, setFilters] = useState<PartialQuizFilters>(
    filterFromSearchParams(searchParams),
  );

  // Persist search query and filters to URL params and localStorage
  useEffect(() => {
    setSearchParams((params) => {
      if (searchQuery) {
        params.set('search', searchQuery);
        localStorage.setItem(`${QUIZ_FUNNELS_CONFIG}:search`, searchQuery);
      } else {
        params.delete('search');
        localStorage.removeItem(`${QUIZ_FUNNELS_CONFIG}:search`);
      }

      if (filters.tags?.length) {
        params.set('tags', JSON.stringify(filters.tags));
        localStorage.setItem(
          `${QUIZ_FUNNELS_CONFIG}:tags`,
          JSON.stringify(filters.tags),
        );
      } else {
        params.delete('tags');
        localStorage.removeItem(`${QUIZ_FUNNELS_CONFIG}:tags`);
      }

      if (filters.statuses?.length) {
        params.set('statuses', JSON.stringify(filters.statuses));
        localStorage.setItem(
          `${QUIZ_FUNNELS_CONFIG}:statuses`,
          JSON.stringify(filters.statuses),
        );
      } else {
        params.delete('statuses');
        localStorage.removeItem(`${QUIZ_FUNNELS_CONFIG}:statuses`);
      }

      if (filters.changes?.length) {
        params.set('changes', JSON.stringify(filters.changes));
        localStorage.setItem(
          `${QUIZ_FUNNELS_CONFIG}:changes`,
          JSON.stringify(filters.changes),
        );
      } else {
        params.delete('changes');
        localStorage.removeItem(`${QUIZ_FUNNELS_CONFIG}:changes`);
      }

      return params;
    });
  }, [searchQuery, filters, setSearchParams]);

  // Fetch quizzes using onSnapshot
  useEffect(() => {
    if (!selectedAccount) {
      setQuizzes(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      quizzesQuery(selectedAccount.id),
      (snapshot) => {
        setQuizzes(snapshot.docs);
      },
      (error) => {
        console.error('Error fetching quizzes:', error);
        Sentry.captureException(error);
        setQuizzes([]);
      },
    );

    return () => unsubscribe();
  }, [selectedAccount]);

  // Apply search and filters
  const filteredQuizzes = useMemo(() => {
    if (!quizzes) return undefined;

    return quizzes.filter((quizDoc) => {
      const quiz = quizDoc.data();

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const nameMatches = quiz.name.toLowerCase().includes(query);
        const idMatches = quizDoc.id.toLowerCase() === query;
        if (!nameMatches && !idMatches) {
          return false;
        }
      }

      // Tags filter
      if (filters.tags?.length) {
        if (!quiz.tags?.some((t) => filters.tags?.includes(t.id))) {
          return false;
        }
      }

      // Status filter
      if (filters.statuses?.length) {
        const quizStatus = quiz.status ?? 'active';
        if (!filters.statuses.includes(quizStatus)) {
          return false;
        }
      }

      // Changes filter
      if (filters.changes?.length) {
        const hasChanges = quiz.hasChangesToPublish ?? false;
        if (!filters.changes.includes(hasChanges)) {
          return false;
        }
      }

      return true;
    });
  }, [quizzes, searchQuery, filters]);

  // Calculate filter options from all quizzes
  const filterOptions = useMemo((): QuizFilters => {
    const tagsMap: Map<string, QuizTag> = new Map();
    quizzes?.forEach((q) => {
      q.data().tags?.forEach((t) => {
        tagsMap.set(t.id, t);
      });
    });

    return {
      tags: Array.from(tagsMap.values()),
      statuses: ['active', 'draft'],
      changes: [true, false],
    };
  }, [quizzes]);

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tags?.length) count++;
    if (filters.statuses?.length) count++;
    if (filters.changes?.length) count++;
    return count;
  }, [filters]);

  const onDeleteQuizClick = useCallback(
    (quizId: string) => {
      if (!selectedAccount) return;

      deleteQuiz(selectedAccount.id, quizId)
        .then(() => {
          show('Deleted!');
        })
        .catch((e) => {
          show('Failed deleting quiz, ' + e.message || e.toString());
          Sentry.captureException(e);
        });
    },
    [selectedAccount, show],
  );

  const [isDuplicating, setIsDuplicating] = useState(false);

  const onDuplicateQuizClick = useCallback(
    async (quiz: Quiz, quizId: string) => {
      if (!selectedAccount || !firebaseUser || isDuplicating) return;

      setIsDuplicating(true);
      track('duplicate_quiz_funnel_button_click', { quizId });

      try {
        const newName = `${quiz.name} (Copy)`;

        const response = await duplicateQuiz(
          selectedAccount.id,
          quizId,
          newName,
        );

        show('Quiz duplicated successfully!');

        // Open the duplicated quiz for editing
        setEditingQuizId(response.quizId);
      } catch (e) {
        const error = e as Error;
        show(
          'Failed to duplicate quiz: ' + (error.message || error.toString()),
          'warn',
        );
        Sentry.captureException(e);
      } finally {
        setIsDuplicating(false);
      }
    },
    [selectedAccount, firebaseUser, isDuplicating, show],
  );

  const renderActions = useCallback(
    (quizDoc: QuizTableItem) => (
      <div className="flex items-center gap-2">
        <Button
          size={'icon'}
          type={'button'}
          variant={'outline'}
          className={'rounded-2lg'}
          onClick={() => {
            track('edit_quiz_funnel_button_click', {
              quizId: quizDoc.id,
            });
            setEditingQuizId(quizDoc.id);
          }}
        >
          <EditPencil className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size={'icon'}
              type={'button'}
              variant={'outline'}
              className={'rounded-2lg'}
            >
              <MoreVert
                strokeWidth={3}
                className="h-4 w-4"
                width={20}
                height={20}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={'bottom'} align={'end'}>
            <DropdownMenuItem
              onClick={() => onDuplicateQuizClick(quizDoc.data(), quizDoc.id)}
            >
              <MultiplePagesEmpty className="h-4 w-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              variant={'destructive'}
              onClick={() => onDeleteQuizClick(quizDoc.id)}
            >
              <Trash className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ),
    [onDeleteQuizClick, onDuplicateQuizClick],
  );

  return (
    <>
      <div className="flex h-full flex-col px-3 py-3 lg:px-10 lg:py-6">
        <div className="flex flex-col items-stretch sm:flex-row sm:items-center">
          <div className="flex w-full flex-col items-center justify-between gap-2 lg:flex-row">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quiz Funnels</h1>
              <p className="mt-1 text-sm text-gray-600">
                Create and manage quiz funnels to engage your audience and boost
                conversions.
              </p>
            </div>
            <div className="flex flex-row gap-2.5">
              <Input
                icon={<Search className="h-5 w-5" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
              />
              <div className="shrink-0">
                <QuizFunnelsFilterPopover
                  filters={filters}
                  filterOptions={filterOptions}
                  onFiltersChange={(newFilters) => {
                    setFilters((currentFilters) => ({
                      ...currentFilters,
                      ...newFilters,
                    }));
                  }}
                  onReset={() => {
                    track('quiz_funnels_filters_reset', {
                      previous_filters_count: activeFilterCount,
                    });
                    setFilters({});
                  }}
                >
                  <Button
                    variant="outline"
                    color="white"
                    className="rounded-2lg relative h-10 w-10"
                  >
                    {activeFilterCount > 0 && (
                      <div className="absolute top-0.5 right-0.5 rounded-md border-2 border-white bg-red-500 px-1 text-xs text-[10px] text-white">
                        {activeFilterCount}
                      </div>
                    )}
                    <Filter className="h-5 w-5" />
                  </Button>
                </QuizFunnelsFilterPopover>
              </div>
              <Button
                type={'button'}
                className="rounded-2lg font-inter h-10 shrink-0 gap-2.5 tracking-[0.01em] has-[>svg]:px-4.5"
                disabled={isCreatingQuiz}
                onClick={async () => {
                  if (!selectedAccount || !firebaseUser) return;
                  track('create_quiz_funnel_button_click');
                  setIsCreatingQuiz(true);
                  try {
                    const response = await createQuiz({
                      accountId: selectedAccount.id,
                      name: 'New Quiz Funnel',
                      path: `quiz-${Date.now()}`,
                      initialFormName: 'Default Form',
                    });
                    setEditingQuizId(response.quizId);
                  } catch (error) {
                    Sentry.captureException(error);
                    show('Failed to create quiz', 'warn');
                  } finally {
                    setIsCreatingQuiz(false);
                  }
                }}
              >
                <Plus className="h-5 w-5" />
                {isCreatingQuiz ? 'Creating...' : 'New Quiz Funnel'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-1 flex-col gap-3">
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-start lg:items-center lg:gap-4">
            <div className="flex w-full shrink-0 flex-row flex-wrap items-stretch gap-2 sm:justify-start lg:items-center lg:gap-4">
              <div>
                <DashboardAnalyticsRangeSelector size="normal" variant="text" />
              </div>
              <div className="flex-1"></div>
              <div className="flex items-center gap-2">
                <Switch
                  size="sm"
                  checked={analyticsType === 'device'}
                  onCheckedChange={(checked) => {
                    track('device_breakdown_toggled', {
                      enabled: checked,
                      source: 'quiz_funnels',
                    });
                    setAnalyticsType(checked ? 'device' : null);
                  }}
                />
                <span className="font-inter text-sm text-gray-800">
                  Breakdown by Device
                </span>
              </div>
            </div>
          </div>

          <div className="relative flex-1">
            <QuizFunnelsTable items={filteredQuizzes} actions={renderActions} />
          </div>
        </div>
      </div>

      {/* Quiz Editor */}
      {selectedAccount &&
        (() => {
          const quiz = quizzes?.find((q) => q.id === editingQuizId)?.data();
          const initialFormId = quiz?.formIds?.[0];
          return (
            <HtmlStepperEditorModal
              quizId={editingQuizId ?? ''}
              initialFormId={initialFormId ?? ''}
              open={!!editingQuizId && !!initialFormId}
              onClose={() => setEditingQuizId(null)}
            />
          );
        })()}
    </>
  );
}
